import { AllProjectsTableControls } from 'components/HomePage/AllProjectsTable/allProjectsTableUtils/AllProjectsTableTypes';
import { GRAPH_COMPUTATION_BY, GRAPH_VIEW, PROGRESS_TYPE, RESOURCE_ELEMENT_TYPE } from 'components/Outputs/ResourceUsage/resourceUsageUtils/resource-usage-enums';
import { AllProjectDisplayViews } from 'components/RootPage/types';
import { GRAPH_TYPE } from 'helpers/graph-utils';
import { METRIC_RESOLUTION } from 'helpers/metricUtils';
import { IMap } from 'helpers/types';
import { ExportFormat } from 'state_management/projects/api';

import { RoleAlias } from '../user_organization_details/types';

export enum ProjectsSortOrder {
  ALPHABETICAL,
  LAST_MODIFIED
}

export enum ProjectFilterBy {
  NONE = 'none',
  MASTER_SCHEDULE = 'master',
  WORKING_SCHEDULE = 'working'
}

export type ResourceRollupReportSettings = {
  computeBy?: GRAPH_COMPUTATION_BY;
  notShowingList?: string[];
  notShowingProjectList?: string[];
  timeResolution?: METRIC_RESOLUTION;
  view?: GRAPH_VIEW;
  graphType?: GRAPH_TYPE;
  progressType?: PROGRESS_TYPE;
  elementType?: RESOURCE_ELEMENT_TYPE;
  columnWidths?: IMap<number>;
  groupBy?: 'resource' | 'project';
};
export enum ProjectPerformanceGanttDetailsOnBarKeys {
  NAME = 'name',
  DATES = 'dates',
  DURATION = 'duration',
  PERCENTAGE = 'percentage'
}

export type ProjectPerformanceGanttDetailsOnBar = Record<ProjectPerformanceGanttDetailsOnBarKeys, boolean>;

export enum ScheduleGanttDetailsOnBarKey {
  SHOW_NAME_ON_GANTT_BAR = 'showNameOnGanttBar',
  SHOW_DURATION_ON_GANTT_BAR = 'showDurationOnGanttBar',
  SHOW_TOTAL_FLOAT_ON_GANTT_BAR = 'showTotalFloatOnGanttBar',
  SHOW_LAG_ON_GANTT_BAR = 'showLagOnGanttBar'
}

export type ScheduleGanttDetailsOnBar = Record<ScheduleGanttDetailsOnBarKey, boolean> & {
  selectedTagOnGanttBar?: string | null; // Custom code GUID to show as text after bar
  selectedTagOnGanttBarForColor?: string | null; // Custom code GUID to color bars by (or 'elementColor' for element colors)
};

export type ALL_PROJECTS_VIEW_SETTINGS = {
  displayView?: AllProjectDisplayViews;
  columnWidths?: IMap<number>;
  columnOrder?: IMap<number>;
  unselectedColumns?: string[];
};
export type ProjectPerformanceReportSettings = {
  gantt?: {
    zoomLevel?: number;
    gridWidth?: number;
    showGrid?: boolean;
    columnWidth?: Record<string, number>;
    columnOrder?: Record<number, string>;
    collapsedWorkspaces?: string[];
    showBaseline?: boolean;
    showHorizontalSightLines?: boolean;
    showVerticalSightLines?: boolean;
    detailsOnBar?: ProjectPerformanceGanttDetailsOnBar;
    showRows?: {
      workspace?: boolean;
      project?: boolean;
    };
  };
};

export interface UserSettings {
  debugging?: boolean;
  consoleLogsEnabled?: boolean;
  projectsSortOrder: ProjectsSortOrder;
  projectsFilterBy: ProjectFilterBy;
  recentProjectIdsMap?: Record<string, string[]>;
  selectedOrganizationId?: string;
  selectedWorkspaceId?: string;
  showPresence?: boolean;
  skipRemoveFiltersDialog?: boolean;
  skipProgressUpdateHelpfulTip?: boolean;
  projectExportFormat?: ExportFormat;
  fieldScheduleNumOfWeeks?: number;
  projectLeftNavExpanded?: boolean;
  defaultOrganizationInviteRole?: RoleAlias;
  defaultProjectInviteRole?: RoleAlias;
  showProductTour?: boolean; // Whether to show the product tour
  contextMenuTooltipShownCount?: number; // Number of times the context menu tooltip has been shown
  busyTooltipShownCount?: number; // Number of times the busy tooltip has been shown
  edgeSessionTooltipShownCount?: number; // Number of times the session tooltip has been shown
  rollingLookaheadBannerDismissed?: boolean; // Whether the rolling lookahead banner has been dismissed
  organizationSettings?: {
    [key: string]: {
      // organization id
      lastViewedOrganizationResourceRollupReportGuid?: string;
      lastViewedOrganizationResourceRollupReportGuidLive?: string;
      lastViewedOrganizationProjectPerformanceReportGuid?: string;
      lastViewedAllProjectsViewGuid?: string;
      isResourceRollupDemoModeEnabled?: boolean;
      isOrgDashboardDemoModeEnabled?: boolean;
      allProjectsViewSettings?: ALL_PROJECTS_VIEW_SETTINGS;
      resourceRollupReportSettings?: {
        [key: string]: {
          // report id
          [key: string]: ResourceRollupReportSettings;
        };
      };
      projectProgressReportSettings?: {
        // report id
        [key: string]: ProjectPerformanceReportSettings;
      };
      reportsUnCheckedDefaultProjectIds?: {
        [key: string]: string[];
        // key = report guid, value = list of project ids
      };
      calendarLibrary?: {
        columnWidths?: IMap<number>;
      };
      resourceLibrary?: {
        columnWidths?: {
          [key: string]: IMap<number>;
        };
      };
      organizationFields?: {
        columnWidths?: IMap<number>;
      };
      usersLibrary?: {
        columnWidths?: IMap<number>;
      };
    };
  };
  // Please keep this list sorted
}
